"use client";

import { useState, useCallback, useMemo } from "react";

export type WizardStep = "upload" | "detect" | "type" | "language" | "confirm";
export type InputMode = "file" | "text";

const FILE_STEP_ORDER: WizardStep[] = [
  "upload",
  "detect",
  "type",
  "language",
  "confirm",
];

// Text mode skips "detect" (no file to detect from)
const TEXT_STEP_ORDER: WizardStep[] = [
  "upload",
  "type",
  "language",
  "confirm",
];

interface WizardState {
  step: WizardStep;
  stepIndex: number;
  totalSteps: number;
  files: File[];
  inputMode: InputMode;
  pastedText: string;
  documentType: string;
  suggestedType: string | null;
  language: string;
  scanning: boolean;
  error: string | null;
  detecting: boolean;
}

interface WizardActions {
  setFiles: (files: File[]) => void;
  setInputMode: (mode: InputMode) => void;
  setPastedText: (text: string) => void;
  setDocumentType: (type: string) => void;
  setSuggestedType: (type: string | null) => void;
  setLanguage: (lang: string) => void;
  setScanning: (scanning: boolean) => void;
  setError: (error: string | null) => void;
  setDetecting: (detecting: boolean) => void;
  next: () => void;
  back: () => void;
  goTo: (step: WizardStep) => void;
  canGoNext: () => boolean;
}

const MIN_TEXT_LENGTH = 50; // Minimum characters for text input

export function useScanWizard(
  initialLanguage: string
): WizardState & WizardActions {
  const [stepIndex, setStepIndex] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [inputMode, setInputMode] = useState<InputMode>("file");
  const [pastedText, setPastedText] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [suggestedType, setSuggestedType] = useState<string | null>(null);
  const [language, setLanguage] = useState(initialLanguage);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);

  const stepOrder = useMemo(
    () => (inputMode === "text" ? TEXT_STEP_ORDER : FILE_STEP_ORDER),
    [inputMode]
  );

  const step = stepOrder[stepIndex];

  const canGoNext = useCallback(() => {
    switch (stepOrder[stepIndex]) {
      case "upload":
        return inputMode === "text"
          ? pastedText.trim().length >= MIN_TEXT_LENGTH
          : files.length > 0;
      case "detect":
        return !detecting;
      case "type":
        return !!documentType;
      case "language":
        return !!language;
      case "confirm":
        return !scanning;
      default:
        return false;
    }
  }, [stepIndex, stepOrder, inputMode, files, pastedText, detecting, documentType, language, scanning]);

  const next = useCallback(() => {
    if (stepIndex < stepOrder.length - 1) {
      setStepIndex((i) => i + 1);
    }
  }, [stepIndex, stepOrder]);

  const back = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
    }
  }, [stepIndex]);

  const goTo = useCallback(
    (target: WizardStep) => {
      const idx = stepOrder.indexOf(target);
      if (idx >= 0) setStepIndex(idx);
    },
    [stepOrder]
  );

  // When switching input mode, reset to step 0
  const handleSetInputMode = useCallback((mode: InputMode) => {
    setInputMode(mode);
    setStepIndex(0);
    // Clear the other mode's data
    if (mode === "text") {
      setFiles([]);
      setSuggestedType(null);
    } else {
      setPastedText("");
    }
  }, []);

  return {
    step,
    stepIndex,
    totalSteps: stepOrder.length,
    files,
    inputMode,
    pastedText,
    documentType,
    suggestedType,
    language,
    scanning,
    error,
    detecting,
    setFiles,
    setInputMode: handleSetInputMode,
    setPastedText,
    setDocumentType,
    setSuggestedType,
    setLanguage,
    setScanning,
    setError,
    setDetecting,
    next,
    back,
    goTo,
    canGoNext,
  };
}
